import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import { Spinner } from "../ui/spinner";
import { Alert, AlertDescription } from "../ui/alert";
import { Skeleton, SkeletonCard, SkeletonTable } from "../ui/skeleton";
import {
  CleanTable,
  CleanTableBody,
  CleanTableCell,
  CleanTableHead,
  CleanTableHeader,
  CleanTableRow,
} from "../ui/clean-table";
import { Badge } from "../ui/badge";
import { Building2, Plus, ArrowRight } from "lucide-react";
import CreateOrganization from "./CreateOrganization";
import { getApiUrl } from "../../config/api";

const OrganizationSelectionPage = () => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl("organizations"), {
        headers: {
          Authorization: `Bearer ${await getAccessTokenSilently()}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch organizations");
      }

      const data = await response.json();
      setOrganizations(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, [getAccessTokenSilently]);

  const handleCreateSuccess = () => {
    setIsCreateDialogOpen(false);
    fetchOrganizations();
  };

  const handleSelectOrganization = (organization) => {
    navigate(`/organization/${organization.id}/dashboard`);
  };

  if (loading) {
    return (
      <div className="p-10 w-full">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between mb-8">
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-96" />
          </div>
          <Skeleton className="h-9 w-40" />
        </div>

        {/* Table Skeleton */}
        <div className="pt-6">
          <SkeletonCard>
            <div className="space-y-4">
              <Skeleton className="h-6 w-48" />
              <SkeletonTable rows={5} columns={4} />
            </div>
          </SkeletonCard>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-10 w-full">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-10 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Organizations</h1>
          <p className="text-muted-foreground">
            Choose an organization to manage its dashboard and business units
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsCreateDialogOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Organization
        </Button>
      </div>

      <div className="pt-6">
        {organizations.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              You don't have access to any organizations yet. Create your first
              organization to get started.
            </p>
            <Button
              className="mt-4"
              onClick={() => setIsCreateDialogOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Organization
            </Button>
          </div>
        ) : (
          <div>
            <CleanTable>
              <CleanTableHeader>
                <CleanTableRow>
                  <CleanTableHead>Organization</CleanTableHead>
                  <CleanTableHead>Description</CleanTableHead>
                  <CleanTableHead>Role</CleanTableHead>
                  <CleanTableHead className="text-right">
                    Actions
                  </CleanTableHead>
                </CleanTableRow>
              </CleanTableHeader>
              <CleanTableBody>
                {organizations.map((organization) => (
                  <CleanTableRow key={organization.id}>
                    <CleanTableCell>
                      <p className="font-medium text-gray-900">
                        {organization.name}
                      </p>
                    </CleanTableCell>
                    <CleanTableCell>
                      <p className="text-gray-600">
                        {organization.description || "No description provided"}
                      </p>
                    </CleanTableCell>
                    <CleanTableCell>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={
                            organization.role === "admin"
                              ? "default"
                              : "secondary"
                          }
                          className="text-xs font-normal"
                        >
                          {organization.role}
                        </Badge>
                        {!organization.approved && (
                          <Badge
                            variant="warning"
                            className="text-xs font-normal"
                          >
                            Pending Approval
                          </Badge>
                        )}
                      </div>
                    </CleanTableCell>
                    <CleanTableCell className="text-right">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleSelectOrganization(organization)}
                        disabled={!organization.approved}
                        className="p-0 h-auto"
                      >
                        {organization.approved ? "View Dashboard →" : "Pending"}
                      </Button>
                    </CleanTableCell>
                  </CleanTableRow>
                ))}
              </CleanTableBody>
            </CleanTable>
          </div>
        )}
      </div>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <CreateOrganization onSuccess={handleCreateSuccess} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizationSelectionPage;
